import type { Dispatch, RefObject } from 'react';

export interface NavItemDimension {
  index: number;
  width: number;
}

export const HELP_BUTTON_FALLBACK_WIDTH = 240;
export const MORE_BUTTON_FALLBACK_WIDTH = 130;
const GAP_WIDTH_PX = 3;

interface NavState {
  isInitialOverflowHidden: boolean;
  moreButtonWidth: number;
  helpButtonWidth: number;
  numberOfOverflowingElements: number | null;
  navigationItemDimensions: NavItemDimension[];
}

export const navigationInitialState: NavState = {
  isInitialOverflowHidden: true,
  moreButtonWidth: MORE_BUTTON_FALLBACK_WIDTH,
  helpButtonWidth: HELP_BUTTON_FALLBACK_WIDTH,
  navigationItemDimensions: [],
  numberOfOverflowingElements: null,
};

type Action =
  | {
      type: 'SET_INITIAL_OVERFLOW_HIDDEN';
      value: boolean;
    }
  | {
      type: 'SET_MORE_BUTTON_WIDTH';
      value: number;
    }
  | {
      type: 'SET_HELP_BUTTON_WIDTH';
      value: number;
    }
  | {
      type: 'SET_OVERFLOWING_ELEMENTS';
      value: number | null;
    }
  | {
      type: 'SET_NAVIGATION_ITEM_DIMENSIONS';
      value: NavItemDimension[];
    };

export const navigationReducer = (state: NavState, { type, value }: Action) => {
  switch (type) {
    case 'SET_INITIAL_OVERFLOW_HIDDEN':
      return { ...state, isInitialOverflowHidden: value };
    case 'SET_MORE_BUTTON_WIDTH':
      return { ...state, moreButtonWidth: value };
    case 'SET_HELP_BUTTON_WIDTH':
      return { ...state, helpButtonWidth: value };
    case 'SET_OVERFLOWING_ELEMENTS':
      return { ...state, numberOfOverflowingElements: value };
    case 'SET_NAVIGATION_ITEM_DIMENSIONS':
      return { ...state, navigationItemDimensions: value };
    default:
      return state;
  }
};

export const calculateOverflowingItemsCount = (
  containerWidth: number,
  items: NavItemDimension[],
  moreButtonWidth: number,
  dispatch: Dispatch<Action>,
) => {
  const numberOfOverflowingItems = calculateOverflowingItems(
    containerWidth,
    items,
    moreButtonWidth,
  );
  dispatch({
    type: 'SET_OVERFLOWING_ELEMENTS',
    value: numberOfOverflowingItems,
  });
};

export const updateMoreButtonWidth = (
  width: number | undefined,
  dispatch: Dispatch<Action>,
) => {
  if (width && width > 0) {
    dispatch({ type: 'SET_MORE_BUTTON_WIDTH', value: width });
  }
};

export const updateHelpButtonWidth = (
  ref: RefObject<HTMLLIElement | null>,
  dispatch: Dispatch<Action>,
) => {
  const helpButtonWidth = ref?.current?.getBoundingClientRect().width;
  if (helpButtonWidth && helpButtonWidth > 0) {
    dispatch({ type: 'SET_HELP_BUTTON_WIDTH', value: helpButtonWidth });
  }
};

export const getNavigationItemsDimensions = (
  items: HTMLCollection,
  dispatch: Dispatch<Action>,
) => {
  const navItems = Array.from(items).map((element, index) => {
    const { width } = element.getBoundingClientRect();

    return { index, width };
  });

  dispatch({
    type: 'SET_NAVIGATION_ITEM_DIMENSIONS',
    value: navItems,
  });

  return navItems;
};

export const calculateOverflowingItems = (
  wrapperWidth: number,
  overflowingItems: NavItemDimension[],
  moreButtonWidth: number,
) => {
  if (wrapperWidth <= 0 || overflowingItems.length <= 0) return 0;

  const mainItems = overflowingItems.slice(0, -1); // Exclude the more button
  const totalGapWidth = GAP_WIDTH_PX * (mainItems.length - 1); //Total gap of the flex items

  let availableWidth = wrapperWidth - totalGapWidth; // Available width for the main items
  let overflowCount = 0; // Number of elements that doesn't fit in the container

  // Calculate the number of overflowing items by incrementing the overflowCount
  for (const { width } of mainItems) {
    if (overflowCount > 0 || width > availableWidth) {
      overflowCount++;
    } else {
      availableWidth -= width;
    }
  }

  // Recursive function to adjust the overflowCount for the more button
  const adjustOverflowForMoreButton = () => {
    const visibleElements = mainItems.slice(0, -overflowCount);
    const gapWidth = GAP_WIDTH_PX * (visibleElements.length - 1);
    if (
      availableWidth - gapWidth + (visibleElements.at(-1)?.width ?? 0) >
      moreButtonWidth
    ) {
      overflowCount++;
    } else {
      overflowCount++;
      adjustOverflowForMoreButton();
    }
  };

  // We're checking if the "More" button fits within the container. If it doesn't, we run the `adjustOverflowForMoreButton` function.
  if (overflowCount > 0 && availableWidth < moreButtonWidth) {
    adjustOverflowForMoreButton();
  }

  return overflowCount;
};
